import { z } from "zod";

// ============================================================================
// COMMON PARAM SCHEMAS (for API routes)
// ============================================================================

/**
 * Generic ID parameter schema for UUID validation
 * Used across multiple routes for consistent validation
 */
export const IdParamSchema = z.object({
  id: z.string().uuid("Invalid ID"),
});

/**
 * Agent ID parameter schema
 */
export const AgentIdParamSchema = z.object({
  agentId: z.string().uuid("Invalid agent ID"),
});

/**
 * Workflow ID parameter schema
 */
export const WorkflowIdParamSchema = z.object({
  workflowId: z.string().uuid("Invalid workflow ID"),
});

/**
 * Phase ID parameter schema
 */
export const PhaseIdParamSchema = z.object({
  phaseId: z.string().uuid("Invalid phase ID"),
});

/**
 * Knowledge Base ID parameter schema
 */
export const KnowledgeBaseIdParamSchema = z.object({
  kbId: z.string().uuid("Invalid knowledge base ID"),
});

/**
 * Patient ID parameter schema
 */
export const PatientIdParamSchema = z.object({
  patientId: z.string().uuid("Invalid patient ID"),
});

/**
 * Follow-up ID parameter schema
 */
export const FollowUpIdParamSchema = z.object({
  followUpId: z.string().uuid("Invalid follow-up ID"),
});

/**
 * Call Session ID parameter schema
 */
export const SessionIdParamSchema = z.object({
  sessionId: z.string().uuid("Invalid session ID"),
});

/**
 * Call Attempt ID parameter schema
 */
export const AttemptIdParamSchema = z.object({
  attemptId: z.string().uuid("Invalid attempt ID"),
});

/**
 * Question ID parameter schema (not UUID, can be any string)
 */
export const QuestionIdParamSchema = z.object({
  questionId: z.string().min(1, "Invalid question ID"),
});

/**
 * Workspace ID parameter schema
 */
export const WorkspaceIdParamSchema = z.object({
  workspaceId: z.string().uuid("Invalid workspace ID"),
});

// ============================================================================
// COMMON QUERY PARAM SCHEMAS
// ============================================================================

/**
 * Pagination query params
 */
export const PaginationQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type IdParam = z.infer<typeof IdParamSchema>;
export type AgentIdParam = z.infer<typeof AgentIdParamSchema>;
export type WorkflowIdParam = z.infer<typeof WorkflowIdParamSchema>;
export type PhaseIdParam = z.infer<typeof PhaseIdParamSchema>;
export type KnowledgeBaseIdParam = z.infer<typeof KnowledgeBaseIdParamSchema>;
export type PatientIdParam = z.infer<typeof PatientIdParamSchema>;
export type FollowUpIdParam = z.infer<typeof FollowUpIdParamSchema>;
export type SessionIdParam = z.infer<typeof SessionIdParamSchema>;
export type AttemptIdParam = z.infer<typeof AttemptIdParamSchema>;
export type QuestionIdParam = z.infer<typeof QuestionIdParamSchema>;
export type WorkspaceIdParam = z.infer<typeof WorkspaceIdParamSchema>;
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;
